import { useMemo, useState } from 'react';
import type { Dataset, SessionUser } from '../../shared/types';
import { computeDashboard, lastCompleteDay, meaningfulCountries, type ChartMetric, type Range, type TabId } from '../lib/compute';
import { Header } from './Header';
import { TeamPanel } from './TeamPanel';
import { Cockpit } from './tabs/Cockpit';
import { Daten } from './tabs/Daten';
import { Kanaele } from './tabs/Kanaele';
import { Produkte } from './tabs/Produkte';
import { Vergleich } from './tabs/Vergleich';
import { Werbung } from './tabs/Werbung';

// Presets that also set the window (the rest — KW/Monate — only change granularity).
const DAY_PRESETS: Range[] = ['Heute', '7 Tage', '30 Tage', 'YTD'];
const shiftDays = (iso: string, n: number) => {
  const dt = new Date(iso + 'T00:00:00Z');
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};
const maxStr = (a: string, b: string) => (a > b ? a : b);
/** Window [from,to] for a preset, clamped to the data span; null = keep current window.
 *  `today` is the dataset's last (partial) day; `lastComplete` is yesterday. Only "Heute"
 *  includes today — every other preset ends on the last complete day. */
function presetWindow(r: Range, since: string, today: string, lastComplete: string): [string, string] | null {
  if (r === 'Heute') return [today, today];
  if (r === '7 Tage') return [maxStr(since, shiftDays(lastComplete, -6)), lastComplete];
  if (r === '30 Tage') return [maxStr(since, shiftDays(lastComplete, -29)), lastComplete];
  if (r === 'YTD') return [maxStr(since, lastComplete.slice(0, 4) + '-01-01'), lastComplete];
  return null; // KW / Monate / Zeitraum keep the current window
}

export function Dashboard({
  data,
  user,
  onSignOut,
}: {
  data: Dataset;
  user: SessionUser | null;
  onSignOut: () => void;
}) {
  const allChannels = useMemo(() => data.channels.map((c) => c.name), [data]);
  const allCountries = useMemo(() => meaningfulCountries(data), [data]);

  const [tab, setTab] = useState<TabId>('cockpit');
  const [range, setRange] = useState<Range>('30 Tage');
  const [selChannels, setSelChannels] = useState<string[]>(allChannels);
  const [selCountries, setSelCountries] = useState<string[]>(allCountries);
  const [openFilter, setOpenFilter] = useState<'channels' | 'countries' | null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  // The current day is partial; every range but "Heute" ends on the last complete day.
  const lastComplete = useMemo(() => lastCompleteDay(data), [data]);
  // The date range is the master window; default to the last-30-days preset (ends yesterday).
  const init30 = useMemo(
    () => presetWindow('30 Tage', data.since, data.until, lastComplete)!,
    [data.since, data.until, lastComplete],
  );
  const [from, setFrom] = useState<string>(init30[0]);
  const [to, setTo] = useState<string>(init30[1]);
  const [metric, setMetric] = useState<ChartMetric>('roas');
  const [adCountry, setAdCountry] = useState<string | null>(null);
  // Cockpit: bucket clicked in the chart → scopes the right-panel breakdown. Reset
  // whenever the window/granularity changes (a stale index would point at a different bucket).
  const [selBucket, setSelBucket] = useState<number | null>(null);

  // Pills: Heute/7T/30T/YTD set the window + day/month granularity; KW/Monate change
  // only granularity (over the current window). Editing the date pickers keeps the
  // active granularity for KW/Monate, else falls back to auto-granularity (Zeitraum).
  const onRange = (r: Range) => {
    const w = presetWindow(r, data.since, data.until, lastComplete);
    if (w) {
      setFrom(w[0]);
      setTo(w[1]);
    }
    setRange(r);
    setSelBucket(null); // bucketing changed → clear the per-bucket breakdown focus
  };
  const onFromChange = (v: string) => {
    setFrom(v);
    if (DAY_PRESETS.includes(range)) setRange('Zeitraum');
    setSelBucket(null);
  };
  const onToChange = (v: string) => {
    setTo(v);
    if (DAY_PRESETS.includes(range)) setRange('Zeitraum');
    setSelBucket(null);
  };

  const vm = useMemo(
    () => computeDashboard(data, { range, selChannels, selCountries, from, to, metric, adCountry, selBucket }),
    [data, range, selChannels, selCountries, from, to, metric, adCountry, selBucket],
  );

  const toggle = (list: string[], name: string) =>
    list.includes(name) ? list.filter((x) => x !== name) : [...list, name];

  return (
    <div className="cw-app">
      <Header
        user={user}
        data={data}
        tab={tab}
        range={range}
        selChannels={selChannels}
        selCountries={selCountries}
        countryOptions={allCountries}
        openFilter={openFilter}
        onTab={(t) => {
          setTab(t);
          setOpenFilter(null);
        }}
        onRange={onRange}
        onToggleFilter={(w) => setOpenFilter((cur) => (cur === w ? null : w))}
        onCloseFilter={() => setOpenFilter(null)}
        onToggleChannel={(n) => setSelChannels((s) => toggle(s, n))}
        onToggleCountry={(n) => setSelCountries((s) => toggle(s, n))}
        onAllChannels={() => setSelChannels((s) => (s.length === allChannels.length ? [] : [...allChannels]))}
        onAllCountries={() => setSelCountries((s) => (s.length === allCountries.length ? [] : [...allCountries]))}
        onSignOut={onSignOut}
        onOpenTeam={() => setTeamOpen(true)}
        from={from}
        to={to}
        minDate={data.since}
        maxDate={data.until}
        onFrom={onFromChange}
        onTo={onToChange}
      />
      {teamOpen && <TeamPanel onClose={() => setTeamOpen(false)} />}
      <main className="cw-main">
        {tab === 'cockpit' && (
          <Cockpit
            vm={vm}
            onMetric={setMetric}
            onSelectBucket={(i) => setSelBucket((cur) => (cur === i ? null : i))}
          />
        )}
        {tab === 'kanaele' && <Kanaele vm={vm} />}
        {tab === 'werbung' && <Werbung vm={vm} adCountry={adCountry} onAdCountry={(c) => setAdCountry((cur) => (cur === c ? null : c))} />}
        {tab === 'produkte' && <Produkte vm={vm} />}
        {tab === 'vergleich' && <Vergleich data={data} selCountries={selCountries} selChannels={selChannels} />}
        {tab === 'daten' && <Daten vm={vm} />}
      </main>
    </div>
  );
}
